import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, resendEmailVerification, logout } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useContextMenu } from '../shared/ContextMenu';
import '../../css/Auth.css';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

const EmailVerification = () => {
    const { user, loading, refreshEmailVerified } = useAuth();
    const [checking, setChecking] = useState(false);
    const [resending, setResending] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const navigate = useNavigate();

    const { handleContextMenu, ContextMenuComponent } = useContextMenu([
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            onClick: () => navigate("/login"),
        },
    ]);

    useEffect(() => {
        if (!loading && !user) {
            navigate('/login');
        }
    }, [user, loading, navigate]);

    // If user becomes verified (e.g. from another tab), redirect
    useEffect(() => {
        if (user && user.emailVerified) {
            navigate('/');
        }
    }, [user, navigate]);

    const showMessage = (text, type = 'info') => {
        setMessage(text);
        setMessageType(type);
        // Auto-clear after 5 seconds
        setTimeout(() => setMessage(''), 5000);
    };

    const handleResendVerification = async () => {
        setResending(true);
        const result = await resendEmailVerification();
        showMessage(result.message, result.success ? 'success' : 'error');
        setResending(false);
    };

    const handleCheckVerification = async () => {
        if (!user) return;

        setChecking(true);
        try {
            // Reload user to get fresh verification status
            const isVerified = await refreshEmailVerified();

            if (isVerified) {
                // Refresh backend verification status
                try {
                    const token = await auth.currentUser.getIdToken(true);
                    await fetch(`${API_BASE}/users/refresh-verification/`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                } catch (err) {
                    // Non-blocking — backend will catch up on next request
                    console.error('Error refreshing backend verification:', err);
                }

                showMessage('Email verified successfully! Redirecting...', 'success');
                setTimeout(() => navigate('/'), 1000);
            } else {
                showMessage('Email not verified yet. Please check your inbox and click the verification link.', 'error');
            }
        } catch (error) {
            console.error('Error checking verification:', error);
            showMessage('Error checking verification status. Please try again.', 'error');
        }
        setChecking(false);
    };

    const handleSignOut = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (err) {
            console.error('Error signing out:', err);
        }
    };

    if (loading) {
        return (
            <div className="Auth">
                <div className="Auth__container">
                    <div className="Auth__header">
                        <h1 className="Auth__brand">Bluebird</h1>
                        <p className="Auth__subtitle">Task Management</p>
                    </div>
                    <div className="Auth__form" style={{ textAlign: 'center' }}>
                        <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) return null;

    return (
        <div className="Auth" onContextMenu={handleContextMenu}>
            <div className="Auth__container">
                <div className="Auth__header">
                    <h1 className="Auth__brand">Bluebird</h1>
                    <p className="Auth__subtitle">Task Management</p>
                </div>

                <div className="Auth__form">
                    <h2 className="Auth__title">Verify Your Email</h2>

                    {message && (
                        <div className={`Auth__message Auth__message--${messageType}`}>
                            {message}
                        </div>
                    )}

                    <div className="Auth__verify-info">
                        <p>We've sent a verification email to:</p>
                        <p className="Auth__verify-email">{user.email}</p>
                        <p className="Auth__verify-hint">
                            Please check your email and click the verification link to continue using Bluebird Task Management.
                        </p>
                        <p className="Auth__verify-hint" style={{ fontSize: '11px', marginTop: '4px' }}>
                            Don't forget to check your spam/junk folder!
                        </p>
                    </div>

                    <button
                        className="Auth__button"
                        onClick={handleCheckVerification}
                        disabled={checking}
                    >
                        {checking ? 'Checking...' : "I've Verified My Email"}
                    </button>

                    <button
                        className="Auth__button Auth__button--secondary"
                        onClick={handleResendVerification}
                        disabled={resending}
                        style={{ marginTop: '12px' }}
                    >
                        {resending ? 'Sending...' : 'Resend Verification Email'}
                    </button>

                    <div className="Auth__footer">
                        <Link to="#" className="Auth__link" onClick={handleSignOut}>
                            Sign in with a different account
                        </Link>
                    </div>
                </div>
            </div>
            {ContextMenuComponent}
        </div>
    );
};

export default EmailVerification;
